import type {MetlTaskBatchPackage, MetlTaskPackage} from "../../../../metl-models/task/MetlTaskBatchPackage.ts";
import {
    RunTypes,
    type SingleTaskBatchScanResult,
    type TaskOverlaps
} from "../../scan-model/GroupedTaskBatchesScanResult.tsx";
import type {TransformationJob} from "../../../../metl-models/jobs/Transformation.ts";
import type {OrchestrationJob} from "../../../../metl-models/jobs/Orchestration.ts";
import {getName} from "../../../../metl-models/jobs/JobObjectHelper.ts";

function calculateTaskOverlaps(tasks: MetlTaskPackage[]): TaskOverlaps {
    if (tasks.length === 0) {
        return {highestOverlap: 0, meanOverlap: 0, meanOverallOverlap: 0};
    }

    // Create events for task starts and ends
    interface TimeEvent {
        time: number;
        type: 'start' | 'end';
    }

    const events: TimeEvent[] = [];

    // Add start and end events for each task
    tasks.forEach(task => {
        events.push({time: task.startTime, type: 'start'});
        events.push({time: task.endTime, type: 'end'});
    });

    // Sort events by time, with 'start' events before 'end' events at the same time
    events.sort((a, b) => {
        if (a.time !== b.time) {
            return a.time - b.time;
        }
        // If times are equal, process 'start' events before 'end' events
        return a.type === 'start' ? -1 : 1;
    });

    let currentOverlap = 0;
    let maxOverlap = 0;
    let totalOverlapTime = 0;
    let totalTime = 0;
    let lastTime = events[0]?.time || 0;

    // Process events to calculate overlaps
    for (const event of events) {
        // Add the time interval with the current overlap count
        const timeDiff = event.time - lastTime;
        if (timeDiff > 0) {
            totalOverlapTime += currentOverlap * timeDiff;
            totalTime += timeDiff;
        }

        // Update current overlap count
        if (event.type === 'start') {
            currentOverlap++;
            maxOverlap = Math.max(maxOverlap, currentOverlap);
        } else {
            currentOverlap--;
        }

        lastTime = event.time;
    }

    const meanOverlap = totalTime > 0 ? totalOverlapTime / totalTime : 0;

    return {
        highestOverlap: maxOverlap,
        meanOverlap: meanOverlap,
        meanOverallOverlap: meanOverlap
    };
}

export function calculateSingleBatchAnalysis(
    jobs: Record<string, TransformationJob | OrchestrationJob> | undefined,
    batchPackage: MetlTaskBatchPackage
): SingleTaskBatchScanResult {
    const componentImplementationTimes: Record<number, number> = {};
    const componentImplementationRowCounts: Record<number, number> = {};
    const componentImplementationRuns: Record<number, number> = {};
    const jobTimes: Record<string, number> = {};
    const tasks = batchPackage.tasks;
    const jobsRun = new Set<string>()

    // Calculate total time for each task and map to components and jobs
    tasks.forEach(task => {
        const taskDuration = task.endTime - task.startTime;

        // Find the job this task belongs to
        jobsRun.add(task.jobName)
        const job = jobs && jobs[task.jobName];
        if (job) {
            // Add time to job total
            jobTimes[task.jobName] = (jobTimes[task.jobName] || 0) + taskDuration;

            // Find the component this task belongs to
            const component = Object.values(job.components).find(comp =>
                comp.id === task.componentId || task.componentName === getName(comp)
            );

            // We're going to use -1 for implId if we can't find the component
            const implId = component?.implementationID ?? -1
            componentImplementationTimes[implId] = (componentImplementationTimes[implId] || 0) + taskDuration;
            componentImplementationRuns[implId] = (componentImplementationRuns[implId] || 0) + 1;
            if (task.rowCount && task.rowCount > 0) componentImplementationRowCounts[implId] = (componentImplementationRowCounts[implId] || 0) + task.rowCount;
        }
    });

    // Calculate overlaps using existing function
    const overlaps = calculateTaskOverlaps(tasks);

    return {
        componentImplementationTimes,
        componentImplementationRowCounts,
        componentImplementationRuns,
        jobTimes,
        jobsRun: Array.from(jobsRun),
        overlaps,
        overallStartTime: batchPackage.startTime,
        overallEndTime: batchPackage.endTime,
        totalDuration: batchPackage.endTime - batchPackage.startTime,
        batchInfo: {
            id: batchPackage.id,
            originatorId: batchPackage.originatorId,
            jobName: batchPackage.jobName,
            groupName: batchPackage.groupName,
            projectName: batchPackage.projectName,
            versionName: batchPackage.versionName,
            environmentName: batchPackage.environmentName,
            state: batchPackage.state,
            source: RunTypes[batchPackage.type]
        }
    };
}